import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BookmarkRecord } from 'fl-bookmarks';
import { ContainerVm, ResourceVm } from 'qp-ui-interface';
import { ClevertapEvent } from 'src/app/analytics/clevertap/enum/clevertap.eventType';
import { ClevertapService } from 'src/app/analytics/clevertap/service/clevertap.service';
import { AppEventName } from 'src/app/analytics/newrelic/enums/EventName';
import { PageType } from 'src/app/analytics/newrelic/enums/PageType';
import { NewrelicService } from 'src/app/analytics/newrelic/services/newrelic.service';
import { NewrelicAppStore } from 'src/app/analytics/newrelic/store/newrelicApp.store';
import { DataService } from 'src/app/data.service';
import { LanguageService } from 'src/app/language.service';
import {
  IN_APP_BANNER_ID,
  isDirectPlayableTypes,
  isSeriesType,
  PlayerContainerType,
} from 'src/app/shared/common/const/common.const';
import { ContentType } from 'src/app/shared/enums/contentType';
import { LocalStorageKeys } from 'src/app/shared/LocalStorageKeys';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { PlaybackService } from 'src/app/shared/services/playback.service';
import { RouteHistoryService } from 'src/app/shared/services/router-history.service';
import { RoutingService } from 'src/app/shared/services/routing.service';
import { FocusRestorationService } from 'src/app/shared/services/utility/focus-restoration.service';
import {
  FocusedCardData,
  ScrollMovement,
} from '../interfaces/cards.interfaces';
import { RouteStrategy } from 'src/app/shared/common/interfaces/common.interface';

@Component({
  selector: 'app-horizontal-carousel',
  templateUrl: './horizontal-carousel.component.html',
  styleUrls: ['./horizontal-carousel.component.scss'],
})
export class HorizontalCarouselComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('carouselContainer') carouselContainer!: ElementRef;
  @ViewChild('horizontalCarousel') horizontalCarousel!: ElementRef;
  @Input() containers: ContainerVm;
  @Input() aspectRatio: string = '2x3';
  @Input() showHeader: boolean = true;
  @Input() isHalfVisible: boolean = false;
  @Input() showPlayIcon: boolean = false;
  @Input() isTitleVisible: boolean = false;
  @Input() customId: string = '';
  @Input() verticalIndex: number = 0;
  @Input() index: number = 0;
  @Input() bookmark: BookmarkRecord | null = null;
  @Input() lang = 'en';
  @Input() playerContainerType: PlayerContainerType | null = null;
  @Input() nowPlayingId: string = '';
  @Input() isSeries: boolean = false;
  @Input() continueWatchingResetId: string | null = null;
  @Input() isDetailPageEpisodeCards: boolean = false;
  @Input() inAppBanner: boolean = false;
  @Input() viewAllIndex: number = 20;
  @Input() isEpisodeList: boolean = false;

  @Output() onCardClick: EventEmitter<boolean | undefined> = new EventEmitter<
    boolean | undefined
  >();
  @Output() focusLeft: EventEmitter<void> = new EventEmitter<void>();
  @Output() isShowParentPin: EventEmitter<{ show: boolean }> =
    new EventEmitter<{ show: boolean }>();

  horizontalList: ResourceVm[] = [];
  isDetailPageCards: boolean = false;
  carouselWidth: number = 0;
  noOfCardsShown: number = 5;
  carouselGap: number = 6;

  scrollMovement: ScrollMovement = { right: '' };
  private actualMovement = 0;
  private focusCardDebounceTimer: any = null;
  private selectedCardDataTimeout: any = null;
  private _lastContinueWatchingResetId: string | null = null;
  private clickDebounceTimer: any = null;
  private isClickDisabled: boolean = false;
  private readonly CLICK_DEBOUNCE_DELAY: number = 800; // 800ms debounce for smart TV compatibility

  constructor(
    private router: Router,
    private dataService: DataService,
    private route: ActivatedRoute,
    private readonly playbackService: PlaybackService,
    private readonly routingService: RoutingService,
    private readonly routeHistoryService: RouteHistoryService,
    private readonly newrelic: NewrelicService,
    private readonly newrelicStore: NewrelicAppStore,
    private readonly languageService: LanguageService,
    private readonly clevertapService: ClevertapService,
    private readonly focusRestorationService: FocusRestorationService,
    private readonly routerHistoryService: RouteHistoryService,
    private readonly dialogService: DialogService
  ) {
    const routeData = this.route.snapshot.data;
    this.isDetailPageCards = routeData.isDetailPageCards;
  }

  ngOnInit(): void {
    this.lang = this.languageService.currLang;
    this.noOfCardsShown = this.getNoOfCardsShown();
    if (this.containers.viewAll && this.containers.viewMoreUrl?.length) {
      this.horizontalList = this.containers.resources.slice(
        0,
        this.viewAllIndex
      );
      let viewAll: ResourceVm = {
        id: this.containers.id,
        key: 'view-all',
        name: 'View All',
        type: 'view-all',
        contentType: 'view-all',
        trailers: [],
        keywords: [],
        collectionID: this.containers.id,
        collectionName: this.containers.name,
      };

      this.horizontalList.push(viewAll);
    } else {
      this.horizontalList = this.containers.resources;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['containers']?.currentValue) {
      this.horizontalList = changes['containers'].currentValue.resources;
    }
    if (
      changes['continueWatchingResetId'] &&
      this.containers.sourceType === 'continue_watching'
    ) {
      this.horizontalList = this.containers.resources;
      this.resetCardTransforms();
    }
  }

  private resetCardTransforms() {
    this.scrollMovement.right = '';
    this.actualMovement = 0;

    const horizontalCarousel = this.horizontalCarousel?.nativeElement;
    horizontalCarousel?.setAttribute('data-index', '0');
  }

  ngAfterViewInit(): void {
    this.getCarouselWidth();
  }

  getCarouselWidth = (): void => {
    this.carouselWidth = this.carouselContainer?.nativeElement.offsetWidth || 0;
  };

  onFocusCard(event: FocusedCardData) {
    this.updateResource(event);
    /* this.clearFocusCardDebounceTimer();
    this.focusCardDebounceTimer = setTimeout(() => {
      this.clearFocusCardDebounceTimer();
    }, 240); */
    if (this.dataService.forcePlayVideo) {
      this.dialogService.closeAll();
      this.dataService.forcePlayVideo = false;
      const playableResource = this.horizontalList.find(
        (it) => it.id === this.dataService.selectedCardData.id
      );
      this.onClick(playableResource);
    }
  }

  onFocusOut() {
    this.clearFocusCardDebounceTimer();
  }

  private updateResource(event: FocusedCardData) {
    if (event.resource) {
      this.dataService.selectedCardData = event.resource;
    }
    if (event.index && event.verticalIndex) {
      this.dataService.horizontalIndex = event.index;
      this.dataService.verticalIndex = event.verticalIndex;
    }
  }

  onArrowLeft() {
    if (this.actualMovement === 0) {
      this.focusLeft.emit();
    } else if (this.actualMovement > 0) {
      this.actualMovement -= 1;
      this.updateCarouselTransform();
    } else {
      // do nothing
    }
  }

  onArrowRight() {
    if (this.actualMovement < this.horizontalList.length - 1) {
      this.actualMovement += 1;
      this.updateCarouselTransform();
    }
  }

  private updateCarouselTransform(): void {
    const track = this.horizontalCarousel?.nativeElement;
    if (track) {
      const firstCard = track.querySelector('.app-cards-0') as HTMLElement;
      if (firstCard) {
        const cardWidth = this.getStyleValue(firstCard, 'width');
        const cardMargin =
          this.getStyleValue(firstCard, 'margin-left') +
          this.getStyleValue(firstCard, 'margin-right');

        this.scrollMovement.right = `${
          (cardWidth + cardMargin) * this.actualMovement
        }px`;
      }
    }
  }

  private getStyleValue(element: HTMLElement, style: string): number {
    return +getComputedStyle(element).getPropertyValue(style).split('px')[0];
  }

  resetInitialPosition(): void {
    this.scrollMovement.right = '';
    this.actualMovement = 0;
  }

  onClick(resource: ResourceVm) {
    // Debouncer - prevent rapid successive clicks
    if (this.isClickDisabled || this.dialogService.hasOpenDialog()) {
      return; // Ignore rapid successive clicks
    }

    // Disable further clicks immediately
    this.isClickDisabled = true;

    // Set timer to re-enable clicks after debounce delay
    this.clickDebounceTimer = setTimeout(() => {
      this.isClickDisabled = false;
      this.clearClickDebounceTimer();
    }, this.CLICK_DEBOUNCE_DELAY);

    this.onClickAction(resource);
  }

  private onClickAction(resource: ResourceVm) {
    if (resource.type !== 'view-all') {
      if (this.nowPlayingId === resource.id) {
        this.onCardClick.emit(true);
        return;
      } else {
        if (!this.isDetailPageEpisodeCards && !this.playerContainerType) {
          this.dataService.selectedCardData = resource;
        }
      }
    }

    localStorage.removeItem(LocalStorageKeys.TAB_CHANGED);
    const { type, id: contentID } = resource;
    if (isDirectPlayableTypes.includes(resource.contentType as ContentType)) {
      this.router.navigate([`player/${type}/${contentID}`], {
        state: {
          type,
          id: contentID,
          analyticsResource: resource,
        },
      });
    } else if (resource.type === 'view-all') {
      this.focusRestorationService.resetIndex();
      const viewAllContainer = Object.assign({}, this.containers);
      this.router.navigate([`/view-all/${resource.id}`], {
        state: {
          viewMoreUrl: this.containers.viewMoreUrl,
          viewMoreContainer: viewAllContainer,
        },
      });
      this.newrelic.sendAppEvent(AppEventName.VIEW_COLLECTION_DETAILS, {
        ...this.newrelicStore.getCollectionInfo(resource),
      });
      this.clevertapService.addEvent(ClevertapEvent.SEE_ALL_CLICKED, {
        trayName: this.containers.name[this.languageService.currLang],
        trayType: this.containers.containerType,
        source: 'TV',
      });
      // TODO: remove this after collection page is checked in tv app, also remove entire setCollectionSelectedCardData() function
      // this.setCollectionSelectedCardData();
      return;
    } else if (resource.type === 'collection') {
      this.focusRestorationService.resetIndex();
      const isGenre = resource.containerName?.en
        ?.toLowerCase()
        ?.includes('genre');
      const container = Object.assign({}, this.containers);
      container.name = resource.title;
      container.id = resource.id;
      if (!resource.contentUrl) {
        return;
      }
      this.newrelic.sendAppEvent(AppEventName.VIEW_COLLECTION_DETAILS, {
        ...this.newrelicStore.getCollectionInfo(resource),
      });
      this.clevertapService.addEvent(ClevertapEvent.SEE_ALL_CLICKED, {
        trayName: this.containers.name[this.languageService.currLang],
        trayType: this.containers.containerType,
        source: 'TV',
      });
      this.router.navigateByUrl(
        `/${isGenre ? 'genres' : 'artist'}/${resource.id}`,
        {
          state: {
            collectionId: resource.id,
            viewMoreUrl: resource.collectionUrl,
            container,
          },
        }
      );
    } else {
      if (this.containers.sourceType === 'continue_watching') {
        const result = this.playbackService.showPopup(
          resource,
          undefined,
          PageType.Browse
        );
        if (!result) {
          this.routingService.navigateToPlayerWithValidation({
            resource,
            navigationPath: `player/${resource.contentType}/${resource.id}`,
            state: {
              isDirectPlayer: true,
              analyticsResource: resource,
            },
          });
        }
      } else {
        if (this.isSeries || this.playerContainerType) {
          if (isSeriesType.includes(resource.contentType as ContentType)) {
            this.dataService.detailResource = null;
            this.dataService.selectedCardData = resource;
            /* AHA-9975
            if (this.playerContainerType === PlayerContainerType.RELATED_ITEMS) {
              this.routeHistoryService.replaceHistory(
                `/${resource.contentType}/${resource.uniqueId}`
              );
              return;
            }
            const history = this.routerHistoryService.history || [];
            const previousRoute = history.length > 1 ? history[history.length - 2] : '';
            const routePath = previousRoute?.split('/')[1] || '';
            const isValidContentType = Object.values(ContentType).includes(
              routePath as any
            ); */
            const history = this.routerHistoryService.history;
            const previousRoute = history[history.length - 2];
            const routePath = previousRoute.split('/')[1];
            const isValidContentType = Object.values(ContentType).includes(
              routePath as any
            );
            if (!isValidContentType || routePath === 'my-aha') {
              this.routeHistoryService.replaceHistory(
                `/${resource.contentType}/${resource.uniqueId}`
              );
              return;
            }
            this.onCardClick.emit();
            return;
          }

          const result = this.playbackService.showPopup(
            resource,
            this.playerContainerType ? RouteStrategy.PopAndReplace : undefined,
            this.isSeries ? PageType.Details : PageType.Player,
            this.playerContainerType
          );
          if (!result) {
            // if (this.isDetailPageEpisodeCards || this.playerContainerType) {
            //   this.dataService.selectedCardData = resource;
            //   this.dataService.detailResource = null;
            // }
            this.routingService.navigateToPlayerWithValidation(
              {
                resource,
                navigationPath: `player/${resource.contentType}/${resource.id}`,
                replaceHistory: Boolean(this.playerContainerType),
              },
              () => {
                this.isShowParentPin.emit({ show: false });
              },
              () => {
                this.isShowParentPin.emit({ show: true });
              }
            );
            return;
          }
          this.onCardClick.emit();
        } else {
          if (this.isDetailPageCards) {
            if (this.dataService.detailResource?.resource?.id === resource.id) {
              this.onCardClick.emit();
              return;
            }
            this.dataService.detailResource = null;
            this.routeHistoryService.replaceHistory(
              `/${resource.contentType}/${resource.uniqueId}`
            );
          } else {
            this.router.navigate([
              `/${resource.contentType}/${resource.uniqueId}`,
            ]);
          }
          this.dataService.currentResourceContainerName =
            (resource.containerName &&
              resource.containerName[this.languageService.currLang]) ||
            undefined;
          this.dataService.currentResourceContainerId =
            resource.containerId || undefined;
          this.dataService.currentResourcePositionInTray =
            resource.positionInTray !== undefined &&
            resource.positionInTray !== null
              ? resource.positionInTray
              : undefined;
        }
      }
    }
  }

  get localizedTitle(): string {
    if (this.containers.id === IN_APP_BANNER_ID) {
      return this.containers.name;
    } else if (this.containers?.localizedName?.[this.lang]?.n) {
      return this.containers?.localizedName?.[this.lang]?.n;
    } else if (this.containers?.localizedName?.['en']?.n === 'Popular List') {
      return this.languageService.getTranslatedText('popular_list');
    } else if (
      this.containers?.localizedName?.['en']?.n === 'Continue Watching'
    ) {
      return this.languageService.getTranslatedText(
        'my_content.continue_watching'
      );
    } else if (this.containers?.localizedName?.['en']?.n === 'Watchlist') {
      return this.languageService.getTranslatedText('watchList');
    } else if (this.containers?.localizedName?.['en']?.n === 'Rentals') {
      return this.languageService.getTranslatedText('my_rentals');
    } else if (this.containers.name === 'episodes-list') {
      const nowPlayingItem = this.containers.resources?.find(
        (it) => it.id === this.nowPlayingId
      );
      return this.languageService.getTranslatedTextWithParams(
        'player_episode_title',
        [nowPlayingItem.seasonNumber, nowPlayingItem.episodeNumber]
      );
    } else if (this.containers.name === 'recommended_for_you') {
      return this.languageService.getTranslatedText('recommended_for_you');
    } else {
      return this.containers?.localizedName?.['en']?.n || '';
    }
  }

  private getNoOfCardsShown(): number {
    const cardConfig = {
      '1x1': {
        default: 8,
      },
      '2x3': {
        default: 7,
        detailPage: 8,
      },
      '9x16': {
        default: 7,
      },
      '16x9': {
        playerPage: 3.5,
        detailPage: 4,
        default: 5,
      },
    };

    const aspectRatio = this.aspectRatio;
    const config = cardConfig[aspectRatio] || cardConfig['2x3'];

    if (aspectRatio === '16x9') {
      if (this.playerContainerType) return config.playerPage;
      if (this.isDetailPageCards) return config.detailPage;
    }

    if (aspectRatio === '2x3' && this.isDetailPageCards) {
      return config.detailPage;
    }

    return config.default;
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }

  ngOnDestroy(): void {
    this.forceClean();
  }

  private clearFocusCardDebounceTimer() {
    if (this.focusCardDebounceTimer) {
      clearTimeout(this.focusCardDebounceTimer);
      this.focusCardDebounceTimer = null;
    }
  }

  private setCollectionSelectedCardData() {
    this.selectedCardDataTimeout = setTimeout(() => {
      if (this.horizontalList && this.horizontalList.length > 0) {
        this.dataService.selectedCardData = this.horizontalList[0];
      }
      this.clearSelectedCardDataTimeout();
    }, 100);
  }

  private clearSelectedCardDataTimeout() {
    if (this.selectedCardDataTimeout) {
      clearTimeout(this.selectedCardDataTimeout);
      this.selectedCardDataTimeout = null;
    }
  }

  private clearClickDebounceTimer(): void {
    if (this.clickDebounceTimer) {
      clearTimeout(this.clickDebounceTimer);
      this.clickDebounceTimer = null;
    }
  }

  private resetClickState(): void {
    this.isClickDisabled = false;
    this.clearClickDebounceTimer();
  }

  private forceClean() {
    this.clearFocusCardDebounceTimer();
    this.clearSelectedCardDataTimeout();
    this.clearClickDebounceTimer();
    this.resetClickState();
    this.carouselContainer = null;
    this.horizontalCarousel = null;
    this.containers = null;
    this.aspectRatio = null;
    this.showHeader = null;
    this.isHalfVisible = null;
    this.showPlayIcon = null;
    this.isTitleVisible = null;
    this.customId = null;
    this.verticalIndex = null;
    this.index = null;
    this.bookmark = null;
    this.lang = null;
    this.playerContainerType = null;
    this.nowPlayingId = null;
    this.isSeries = null;
    this.continueWatchingResetId = null;
    this.isDetailPageEpisodeCards = null;
    this.inAppBanner = null;
    this.viewAllIndex = null;
    this.isEpisodeList = null;
    this.horizontalList = null;
    this.isDetailPageCards = null;
    this.carouselWidth = null;
    this.noOfCardsShown = null;
    this.carouselGap = null;
    this.scrollMovement = null;
    this.actualMovement = null;
    this._lastContinueWatchingResetId = null;
    this.onCardClick.complete();
    this.focusLeft.complete();
    this.isShowParentPin.complete();
  }
}
